import React from 'react';
import { Icon } from '../icon/Icon.jsx';
import { Wordmark } from './Wordmark.jsx';

/* 224px expanded, 56px icon-collapsed, --bg-inset, 1px right border. Grouped by
   WHAT YOU CAME HERE TO DO — RUN, DECIDE, CAPITAL, then an unlabelled utility
   group. Group headings disappear when collapsed (the boundaries survive as
   spacing). Only TWO badges ever appear: Fleet's candidate count, and a circle on
   Intel when a source is degraded. Wallet's link-2 is a label, not a
   notification. A sidebar full of counts is a sidebar you stop reading. */

export const NAV = [
  { group: 'RUN', items: [
    { id: 'dashboard', label: 'Dashboard', icon: 'gauge', key: 'd' },
    { id: 'strategies', label: 'Strategies', icon: 'list', key: 's' },
    { id: 'fleet', label: 'Fleet', icon: 'layers', key: 'f' },
    { id: 'orders', label: 'Orders', icon: 'receipt', key: 'o' },
  ] },
  { group: 'DECIDE', items: [
    { id: 'research', label: 'Research', icon: 'flask-conical', key: 'r' },
    { id: 'markets', label: 'Markets', icon: 'candlestick-chart', key: 'm' },
    { id: 'intel', label: 'Intel', icon: 'radio', key: 'i' },
  ] },
  { group: 'CAPITAL', items: [
    { id: 'books', label: 'Books', icon: 'book-open', key: 'b' },
    { id: 'portfolio', label: 'Portfolio', icon: 'pie-chart', key: 'p' },
    { id: 'wallet', label: 'Wallet', icon: 'wallet', key: 'w', glyph: 'link-2' },
    { id: 'reports', label: 'Reports', icon: 'file-text', key: 'e' },
  ] },
  { group: null, items: [
    { id: 'learn', label: 'Learn', icon: 'graduation-cap', key: 'l' },
    { id: 'system', label: 'System', icon: 'server', key: 'y' },
    { id: 'settings', label: 'Settings', icon: 'settings', key: 'g' },
  ] },
];

export function Sidebar({ active, onNavigate, collapsed = false, groups = NAV, badges = {}, engineOk = true, brand = 'JM-Trade', style }) {
  return (
    <nav style={{
      width: collapsed ? 'var(--sidebar-w-collapsed)' : 'var(--sidebar-w)', flex: '0 0 auto',
      background: 'var(--bg-inset)', borderRight: '1px solid var(--border-default)',
      display: 'flex', flexDirection: 'column', overflow: 'hidden', ...style,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', height: 'var(--header-h)', padding: collapsed ? 0 : '0 var(--space-20)',
        justifyContent: collapsed ? 'center' : 'flex-start', flex: '0 0 auto',
      }}>
        {/* The mark, not the product name in a span. Collapsed shows the icon alone — which
            is what the 56px rail has room for and what the mark exists for — and expanded
            shows the horizontal lockup. `--accent` rather than `--text-primary`: this is
            the one place in the product where the accent means *brand* rather than
            *interactive*, and it is a static element so there is no ambiguity to create.

            `brand` still overrides both, for a licensee deployment. */}
        {brand !== 'JM-Trade' ? (
          <span style={{
            fontSize: 'var(--heading-size)', fontWeight: 'var(--heading-weight)',
            letterSpacing: 'var(--heading-tracking)', color: 'var(--text-primary)',
            whiteSpace: 'nowrap',
          }}>{collapsed ? brand.slice(0, 2) : brand}</span>
        ) : (
          <Wordmark
            variant={collapsed ? 'mark' : 'horizontal'}
            style={{ width: collapsed ? 24 : 132, color: 'var(--accent)' }}
          />
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--space-8) 0' }}>
        {groups.map((g, gi) => (
          <div key={g.group || 'utility-' + gi} style={{ marginTop: gi ? 'var(--space-20)' : 0 }}>
            {g.group && !collapsed ? (
              <div style={{
                padding: '0 var(--space-20) var(--space-4)', fontSize: 'var(--label-size)',
                letterSpacing: 'var(--label-tracking)', fontWeight: 'var(--label-weight)',
                textTransform: 'uppercase', color: 'var(--text-tertiary)',
              }}>{g.group}</div>
            ) : null}
            {g.items.map((it) => {
              const on = active === it.id;
              const badge = badges[it.id];
              return (
                <button key={it.id} type="button" onClick={() => onNavigate && onNavigate(it.id)}
                  title={collapsed ? it.label : undefined} className="jmt-nav jmt-press"
                  data-on={on ? '' : undefined} aria-current={on ? 'page' : undefined}
                  style={{
                    position: 'relative',
                    display: 'flex', alignItems: 'center', gap: 'var(--space-12)', width: '100%',
                    height: 32, padding: collapsed ? 0 : '0 var(--space-20)',
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    background: on ? 'var(--accent-subtle)' : 'transparent',
                    border: 'none',
                    color: on ? 'var(--accent)' : 'var(--text-secondary)',
                    cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--font-ui)', fontSize: 'var(--body-sm-size)',
                  }}>
                  <Icon name={it.icon} size={16} />
                  {collapsed ? null : (
                    <>
                      <span style={{ flex: 1 }}>{it.label}</span>
                      {it.glyph ? <Icon name={it.glyph} size={13} style={{ color: 'var(--text-tertiary)' }} /> : null}
                      {badge != null ? (
                        typeof badge === 'number'
                          ? <span style={{ fontSize: 'var(--caption-size)', color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{badge}</span>
                          : <Icon name="circle" size={9} filled style={{ color: 'var(--attn)' }} />
                      ) : null}
                    </>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', gap: 'var(--space-8)', height: 32,
        padding: collapsed ? 0 : '0 var(--space-20)', justifyContent: collapsed ? 'center' : 'flex-start',
        borderTop: '1px solid var(--border-subtle)', flex: '0 0 auto',
        fontSize: 'var(--caption-size)', color: engineOk ? 'var(--text-secondary)' : 'var(--loss)',
      }}>
        <Icon name="circle" size={9} filled style={{ color: engineOk ? 'var(--profit)' : 'var(--loss)' }} />
        {collapsed ? null : (engineOk ? 'engine ok' : 'engine down')}
      </div>
      <style>{`
        /* The active marker is a pseudo-element that grows from the item's vertical
           centre, not a border that blinks between two items. 2px wide, --accent, and
           it is the only thing in the sidebar that moves on navigation: scaleY from 0
           to 1 in --dur-menu reads as "the selection travelled here" where a border
           swap reads as a repaint. transform-origin is centre so it opens both ways. */
        .jmt-nav::before{
          content:"";position:absolute;left:0;top:0;bottom:0;width:2px;
          background:var(--accent);transform:scaleY(0);transform-origin:center;
          transition:transform var(--dur-menu) var(--ease-out);
        }
        .jmt-nav[data-on]::before{transform:scaleY(1)}
        .jmt-nav:hover:not([data-on]){background:var(--bg-surface);color:var(--text-primary)}
        .jmt-nav[data-on]:hover{background:var(--accent-subtle)}
        .jmt-nav:focus-visible{outline:none;box-shadow:var(--focus-ring)}
      `}</style>
    </nav>
  );
}
